import { useEffect, useId, useMemo, useState } from 'react';
import {
  byCategory,
  byLocation,
  byTag,
  compositionAsAt,
  fieldValue,
  intentionSpread,
  reportableItems,
  reviewCoverage,
  today,
  type AnyEvent,
  type CompositionAsAt,
  type GroupRow,
  type Manifest,
  type Money,
} from '@intentionalist/shared';
import { formatAmount } from './money.js';
import type { Replica } from './replica.js';

/**
 * Insight reports (Epic 9).
 *
 * The epic asks the catalog to "tell me things I would not have noticed", so
 * each report here answers a question a list cannot: what a category is actually
 * worth, how much of the
 * catalog has been looked at in a year.
 *
 * Every group can be opened to see what composes it (FR-9.9). That is not a
 * convenience -- an aggregate nobody can drill into is a number to be taken on
 * faith, and the whole argument of the product is that the manifest earns trust
 * by being checkable.
 */
type Panel = 'holdings' | 'intentions' | 'coverage' | 'history';

const PANELS: { id: Panel; label: string }[] = [
  { id: 'holdings', label: 'Holdings' },
  { id: 'intentions', label: 'Verdicts' },
  { id: 'coverage', label: 'Review coverage' },
  { id: 'history', label: 'Back then' },
];

export function Reports({
  manifest,
  version,
  replica,
}: {
  manifest: Manifest;
  version: number;
  replica: Replica;
}) {
  const [panel, setPanel] = useState<Panel>('holdings');
  const ids = { panel: useId() };

  return (
    <div className="review">
      <h2>Reports</h2>

      {/*
        Named, because these are not the tabs above them.
        "Reports" and "Export" move between screens; these six change what this
        screen shows. The stylesheet now draws the two differently -- a rule
        under a tab, a pill for a setting -- and the caption says which this is.
      */}
      <div className="field">
        <span className="field-label" id={ids.panel}>
          Report
        </span>
        <div className="choice" role="group" aria-labelledby={ids.panel}>
          {PANELS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={panel === option.id}
              onClick={() => setPanel(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {panel === 'holdings' && <Holdings manifest={manifest} version={version} />}
      {panel === 'intentions' && <Verdicts manifest={manifest} version={version} />}
      {panel === 'coverage' && <Coverage manifest={manifest} version={version} />}
      {panel === 'history' && <BackThen replica={replica} version={version} manifest={manifest} />}
    </div>
  );
}

const Totals = ({ totals }: { totals: Money[] }) =>
  totals.length === 0 ? (
    <span className="muted">—</span>
  ) : (
    <>
      {totals.map((total, index) => (
        <span key={total.currency}>
          {index > 0 && ' · '}
          {formatAmount(total, { ambiguous: totals.length > 1 })}
        </span>
      ))}
    </>
  );

/**
 * A count report whose rows open (FR-9.9).
 *
 * The membership comes from the row itself rather than being recomputed on
 * expansion, so the list can never disagree with the count above it.
 */
function GroupTable({
  rows,
  label,
  manifest,
  nested,
}: {
  rows: GroupRow[];
  label: string;
  manifest: Manifest;
  /** Whether a nested figure is meaningful, which only a tree has. */
  nested: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);

  if (rows.length === 0) return <p className="muted">Nothing to report yet.</p>;

  return (
    <ul className="catalog" aria-label={label}>
      {rows.map((row) => {
        const key = row.groupId ?? '__none__';
        const expanded = open === key;
        return (
          <li key={key}>
            <div className="catalog-row">
              <div>
                <strong>{row.label}</strong>
                <div className="muted">
                  {row.count} item{row.count === 1 ? '' : 's'} · <Totals totals={row.value} />
                  {nested && row.nestedCount !== row.count && (
                    <>
                      {' · '}
                      {row.nestedCount} including nested · <Totals totals={row.nestedValue} />
                    </>
                  )}
                </div>
              </div>
              <div className="actions">
                <button
                  type="button"
                  aria-label={`${expanded ? 'Hide' : 'Show'} items in ${row.label}`}
                  aria-expanded={expanded}
                  disabled={row.itemIds.length === 0}
                  onClick={() => setOpen(expanded ? null : key)}
                >
                  {expanded ? 'Hide' : 'Show items'}
                </button>
              </div>
            </div>

            {expanded && <ItemNames manifest={manifest} itemIds={row.itemIds} label={row.label} />}
          </li>
        );
      })}
    </ul>
  );
}

function ItemNames({
  manifest,
  itemIds,
  label,
}: {
  manifest: Manifest;
  itemIds: readonly string[];
  label: string;
}) {
  const named = itemIds
    .flatMap((itemId) => {
      const item = manifest.items.get(itemId);
      return item === undefined ? [] : [item];
    })
    .map((item) => ({
      itemId: item.itemId,
      name: String(fieldValue(item, 'name') ?? '(unnamed)'),
      price: fieldValue(item, 'purchasePrice') ?? null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <ul className="chips" aria-label={`Items in ${label}`}>
      {named.map((entry) => (
        <li key={entry.itemId}>
          {entry.name}
          {entry.price !== null && <span className="muted"> {formatAmount(entry.price)}</span>}
        </li>
      ))}
    </ul>
  );
}

/** FR-9.1 and FR-9.8 together: what is held, by category, location and tag. */
function Holdings({ manifest, version }: { manifest: Manifest; version: number }) {
  const id = useId();
  const [grouping, setGrouping] = useState<'category' | 'location' | 'tag'>('category');

  const rows = useMemo(() => {
    if (grouping === 'category') return byCategory(manifest);
    if (grouping === 'location') return byLocation(manifest);
    return byTag(manifest);
  }, [manifest, version, grouping]);

  const items = useMemo(() => reportableItems(manifest), [manifest, version]);

  return (
    <>
      <dl className="sync-state">
        <dt>Items owned</dt>
        <dd>{items.length}</dd>
      </dl>

      <div className="field">
        <label htmlFor={id}>Grouped by</label>
        <select
          id={id}
          value={grouping}
          onChange={(e) => setGrouping(e.target.value as 'category')}
        >
          <option value="category">Category</option>
          <option value="location">Location</option>
          <option value="tag">Tag</option>
        </select>
      </div>

      {grouping === 'tag' && (
        <p className="muted hint">
          An item with several tags is counted under each, so these do not add up to the catalog.
        </p>
      )}

      <GroupTable
        rows={rows}
        label={`Holdings by ${grouping}`}
        manifest={manifest}
        nested={grouping !== 'tag'}
      />
    </>
  );
}

/** FR-9.2. Proportions, in a fixed order, with nothing marked as the right answer. */
function Verdicts({ manifest, version }: { manifest: Manifest; version: number }) {
  const spread = useMemo(() => intentionSpread(manifest), [manifest, version]);
  const [open, setOpen] = useState<string | null>(null);

  if (spread.total === 0) return <p className="muted">Nothing catalogued yet.</p>;

  return (
    <ul className="catalog" aria-label="Verdicts">
      {spread.rows.map((row) => {
        const expanded = open === row.intention;
        return (
          <li key={row.intention}>
            <div className="catalog-row">
              <div>
                <strong>{row.intention[0]!.toUpperCase() + row.intention.slice(1)}</strong>
                <div className="muted">
                  {row.count} of {spread.total} · {Math.round(row.share * 100)}% ·{' '}
                  <Totals totals={row.value} />
                </div>
              </div>
              <div className="actions">
                <button
                  type="button"
                  aria-label={`Show items marked ${row.intention}`}
                  aria-expanded={expanded}
                  disabled={row.count === 0}
                  onClick={() => setOpen(expanded ? null : row.intention)}
                >
                  {expanded ? 'Hide' : 'Show items'}
                </button>
              </div>
            </div>

            {expanded && (
              <ItemNames manifest={manifest} itemIds={row.itemIds} label={row.intention} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** FR-9.7. Two figures, because never looked at is a different problem from looked at long ago. */
function Coverage({ manifest, version }: { manifest: Manifest; version: number }) {
  const coverage = useMemo(() => reviewCoverage(manifest, 365, today()), [manifest, version]);

  return (
    <dl className="sync-state">
      <dt>Reviewed in the last year</dt>
      <dd>
        {coverage.reviewed} of {coverage.total} · {Math.round(coverage.share * 100)}%
      </dd>

      <dt>Never reviewed</dt>
      <dd>{coverage.neverReviewed}</dd>
    </dl>
  );
}

/**
 * FR-9.10: the manifest as it stood on a past date.
 *
 * Reads the local event log and replays a prefix of it through the same merge,
 * which is the only reason this question is answerable at all -- an in-place
 * rename or an outright delete would have destroyed the past. The log can be
 * long, so it is loaded once when the panel opens rather than on every keystroke.
 */
function BackThen({
  replica,
  version,
  manifest,
}: {
  replica: Replica;
  version: number;
  /** The present, to compare the past against. */
  manifest: Manifest;
}) {
  const id = useId();
  const [log, setLog] = useState<AnyEvent[] | null>(null);
  const [asAt, setAsAt] = useState(() => {
    const at = new Date();
    at.setUTCFullYear(at.getUTCFullYear() - 1);
    return at.toISOString().slice(0, 10);
  });
  const [shown, setShown] = useState<CompositionAsAt | null>(null);
  const [fault, setFault] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void replica
      .log()
      .then((events) => {
        if (live) setLog(events);
      })
      .catch(() => {
        if (live) setFault('The local history could not be read.');
      });
    return () => {
      live = false;
    };
  }, [replica, version]);

  const look = () => {
    if (log === null) return;
    try {
      setShown(compositionAsAt(log, asAt, manifest));
      setFault(null);
    } catch {
      setFault('That is not a date.');
    }
  };

  return (
    <>
      <div className="field">
        <label htmlFor={id}>What I owned on</label>
        <input id={id} type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
        <button type="button" onClick={look} disabled={log === null}>
          {log === null ? 'Reading history…' : 'Look'}
        </button>
        {fault !== null && (
          <p className="warn" role="alert">
            {fault}
          </p>
        )}
      </div>

      {shown !== null && (
        <>
          <dl className="sync-state">
            <dt>Items owned on {shown.asAt}</dt>
            <dd>{shown.items}</dd>

            <dt>Items owned now</dt>
            {/*
              The comparison is the point of the screen, so the direction is
              stated rather than left to be worked out from two numbers. "No
              change" rather than "0 more", which reads as a missing figure.
            */}
            <dd>
              {shown.itemsNow} <span className="muted">({describeChange(shown)})</span>
            </dd>

            <dt>Worth, then</dt>
            <dd>
              <Totals totals={shown.value} />
            </dd>

            <dt>Catalogued since</dt>
            <dd>{shown.catalogedSince}</dd>

            <dt>Released since</dt>
            <dd>{shown.releasedSince}</dd>
          </dl>

          {/*
            Said once, here, because the two "since" figures are flows and the
            two totals are stocks. An item catalogued and released inside the
            same period is in both, and a deleted one is in neither, so the
            arithmetic need not close -- and someone checking it deserves to
            know that before they start.
          */}
          <p className="muted hint">
            Counted when each thing happened, so these need not add up to the difference between the
            two totals.
          </p>

          <h3>By category, then</h3>
          <HistoricRows rows={shown.byCategory} label="Categories then" />

          <h3>By tag, then</h3>
          <HistoricRows rows={shown.byTag} label="Tags then" />
        </>
      )}
    </>
  );
}

/**
 * Historic rows do not open.
 *
 * The names are as they were on the date in question, and the items behind them
 * may since have been deleted or renamed. Linking into the present catalog
 * from a past figure would quietly mix the two, which is the confusion this
 * report exists to avoid.
 */
const HistoricRows = ({ rows, label }: { rows: GroupRow[]; label: string }) =>
  rows.length === 0 ? (
    <p className="muted">Nothing filed under one, then.</p>
  ) : (
    <ul className="catalog" aria-label={label}>
      {rows.map((row) => (
        <li key={row.groupId ?? '__none__'}>
          <div className="catalog-row">
            <div>
              <strong>{row.label}</strong>
              <div className="muted">
                {row.count} item{row.count === 1 ? '' : 's'} · <Totals totals={row.value} />
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

/** The change between the two totals, in words rather than as a signed number. */
function describeChange(shown: CompositionAsAt): string {
  const difference = shown.itemsNow - shown.items;
  if (difference === 0) return 'no change';
  const noun = Math.abs(difference) === 1 ? 'item' : 'items';
  return difference > 0 ? `${difference} more ${noun}` : `${-difference} fewer ${noun}`;
}
